import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { suggestedActions, SuggestedActionType } from '../../../mocks/data/itemRationalisationData';

const actionColor: Record<SuggestedActionType, 'info' | 'error' | 'warning' | 'default'> = {
  Merge: 'info',
  Retire: 'error',
  FlagMissingData: 'warning',
  ReplaceInRecipes: 'default',
};

/** Consolidated queue of every suggested action across Duplicates/Unused/Missing Data tabs -
 * a single worklist for central teams. "Apply" only updates local UI state - no backend
 * persistence exists for these actions yet. */
const SuggestedActionsTab = () => {
  const { t } = useTranslation('productManagement');
  const [applied, setApplied] = useState<Set<string>>(new Set());

  return (
    <Stack spacing={2}>
      <Typography variant="textSM"
        color="text.secondary">
        {t('rationalisation.suggestedActionsIntro', 'A single worklist of every suggested clean-up action across the catalogue.')}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('itemNumber', 'Item Number')}</TableCell>
            <TableCell>{t('description', 'Description')}</TableCell>
            <TableCell>{t('rationalisation.action', 'Action')}</TableCell>
            <TableCell>{t('rationalisation.detail', 'Detail')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {suggestedActions.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.itemNumber}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>
                <Chip label={t(`rationalisation.actionType.${item.action}`, item.action)}
                  size="small"
                  color={actionColor[item.action]} />
              </TableCell>
              <TableCell>{item.detail}</TableCell>
              <TableCell>
                {applied.has(item.id) ? (
                  <Chip label={t('rationalisation.applied', 'Applied')}
                    color="success"
                    size="small" />
                ) : (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setApplied((prev) => new Set(prev).add(item.id))}
                  >
                    {t('rationalisation.apply', 'Apply')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};

export default SuggestedActionsTab;
